import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelectPreviewArchivingData } from 'redux/hooks/archiving';
import { CustomTable } from './controls/customTable';
import RMSvgIcon from 'components/customComponents/CommonComponents/RMSvgIcon';
import {
  ARCHIVING_PAUSE_MODAL_SUBTITLE,
  ARCHIVING_PAUSE_MODAL_WARNING_MESSAGE,
  ARCHIVING_START_OVER_INCLUDE_ARCHIVED_CHECKBOX_LABEL,
  ARCHIVING_START_OVER_MODAL_SUBTITLE,
  ARCHIVING_START_OVER_MODAL_WARNING_MESSAGE,
  ARCHIVING_START_OVER_TITLE,
  ARCHIVING_STORAGE_ERROR_DESCRIPTION,
  ARCHIVING_STORAGE_ERROR_HEADING,
  ARCHIVING_STORAGE_ERROR_TITLE,
  ARCHIVING_STORAGE_INVALID_DESCRIPTION,
  ARCHIVING_STORAGE_INVALID_TITLE,
  PREVIEW_TABLE_PAGE_SIZE,
  PreviewTableColumns,
} from './constants';
import { FmlxBanner, FmlxButton, FmlxModal, FmlxProgressBar } from 'fmlx-common-ui';
import { useBackdropLoading, useModalHandlers } from 'components/modal';
import { ArchiveExperimentModalBody } from './archiveExperimentModal';
import { useRMDispatch } from 'redux/hooks';
import {
  pauseArchivingThunk,
  resumeArchivingThunk,
  runArchivingThunk,
  RunArchivingRejection,
  startOverArchivingThunk,
} from 'redux/thunks/archiving';
import { resetArchivingResults } from 'redux/reducers/archivingReducer';
import {
  IPreviewResultRowDTO,
  ResponseType,
  RunArchivingDTO,
  RunArchivingExperimentDTO,
} from 'business/models/Models';
import ArchivingService from 'business/services/ArchivingService';
import { showAlertSnackBar } from 'utils/Global';
import EAlertType from 'enums/EAlertType';
import ESnackBarPosition from 'enums/ESnackBarPosition';
import { useIsArchivingJobActive, useIsArchivingJobPaused } from './hooks';
import {
  ArchivingStorageStatus,
  getArchivingStorageNotice,
  getArchivingStorageStatus,
  getPreviewTableView,
  PreviewTableView,
} from './utils';
import './styles.css';
import { ModalCheckboxFooter } from './controls/ModalCheckboxFooter';

type SelectionCounts = {
  total: number;
  selected: number;
  selectedExperimentIds: number[];
};

type PreviewTableProps = {
  onStartOverComplete?: () => void;
  storageStatus?: ArchivingStorageStatus;
  hasSelection?: boolean;
};

export const PreviewTable = ({
  onStartOverComplete,
  storageStatus = 'ok',
  hasSelection = false,
}: PreviewTableProps) => {
  const previewArchivingData = useSelectPreviewArchivingData();
  const dispatch = useRMDispatch();
  const { showModal, hideModal } = useModalHandlers();
  const { showBackdrop, hideBackdrop } = useBackdropLoading();
  const isJobActive = useIsArchivingJobActive();
  const isJobPaused = useIsArchivingJobPaused();
  const [counts, setCounts] = useState<SelectionCounts>({
    total: 0,
    selected: 0,
    selectedExperimentIds: [],
  });
  const archiveModalInstanceId = useRef(0);

  const data = useMemo(
    () =>
      previewArchivingData.data
        .filter((item) => !item.isArchived)
        .map((item) => {
          return {
            name: item.name,
            dateCreated: item.dateCreated?.toLocaleDateString('en-US'),
            path: item.path,
            inspectionDate: item.inspectionDate?.toLocaleDateString('en-US'),
            isArchived: item.isArchived ? 'Archived' : 'Active',
            __experimentId: item.id,
          };
        }),
    [previewArchivingData.data],
  );

  // Submits the given rows for archiving. When the backend reports that some of them are
  // currently open by other users, offers to exclude just those and resubmit the rest,
  // rather than hard-blocking the whole request.
  const runArchiving = useCallback(
    async (archivingName: string, rows: IPreviewResultRowDTO[]) => {
      const request = new RunArchivingDTO({
        name: archivingName,
        experiments: rows.map(
          (row) =>
            new RunArchivingExperimentDTO({
              experimentId: row.id,
              experimentName: row.name,
              originalLocation: row.path,
              dateCreated: row.dateCreated,
              inspectionDate: row.inspectionDate,
            }),
        ),
      });

      showBackdrop('Archiving...');
      try {
        const result = await dispatch(runArchivingThunk(request)).unwrap();
        hideModal();
        showAlertSnackBar(
          EAlertType.SUCCESS,
          ESnackBarPosition.TOP,
          result.message || 'Archiving job queued successfully.',
        );
      } catch (error) {
        const rejection = error as RunArchivingRejection;
        if (rejection?.openExperiments?.length) {
          const openIds = new Set(
            rejection.openExperiments.map((exp) => exp.experimentId),
          );
          showModal({
            heading: 'Archive Experiment',
            body: (
              <div>
                <FmlxBanner
                  variant={FmlxBanner.Variant.ERROR}
                  title={<div className="text-sm">Experiment Is Opened</div>}
                  show
                  hideCloseButton
                />
                <div className="text-sm my-3 text-[#5D5D5D]">
                  Some experiments are currently opened by other users, do you want to
                  exclude them from the archiving process?
                </div>
              </div>
            ),
            okButtonText: 'Exclude Experiment',
            okButtonType: FmlxButton.Type.PRIMARY,
            noButtonText: 'Close',
            noButtonType: FmlxButton.Type.BASIC,
            useDefaultButtonLayout: true,
            onOk: () =>
              runArchiving(
                archivingName,
                rows.filter((row) => !openIds.has(row.id)),
              ),
            onHide: hideModal,
          });
          return;
        }

        showAlertSnackBar(
          EAlertType.ERROR,
          ESnackBarPosition.TOP,
          rejection?.message || 'Failed to start archiving job.',
        );
      } finally {
        hideBackdrop();
      }
    },
    [dispatch, hideBackdrop, hideModal, showBackdrop, showModal],
  );

  const handleRunArchiving = useCallback(
    (archivingName: string) => {
      const selectedRows = previewArchivingData.data.filter((row) =>
        counts.selectedExperimentIds.includes(row.id),
      );

      return runArchiving(archivingName, selectedRows);
    },
    [counts.selectedExperimentIds, previewArchivingData.data, runArchiving],
  );

  const handleArchiveClick = useCallback(() => {
    archiveModalInstanceId.current += 1;
    showModal({
      heading: 'Archive Experiment',
      id: 'customWidthModal',
      size: FmlxModal.Size.MEDIUM,
      hideFooter: true,
      onHide: hideModal,
      body: (
        <ArchiveExperimentModalBody
          key={archiveModalInstanceId.current}
          experimentCount={counts.selected}
          onClose={hideModal}
          onRunArchiving={handleRunArchiving}
        />
      ),
    });
  }, [counts.selected, handleRunArchiving, hideModal, showModal]);

  const showStorageErrorModal = useCallback(
    (status: 'full' | 'invalid') => {
      const isFull = status === 'full';
      showModal({
        heading: ARCHIVING_STORAGE_ERROR_HEADING,
        isOkOnly: true,
        okButtonText: 'Close',
        onOk: hideModal,
        onHide: hideModal,
        inlineMessage: {
          type: 'error',
          message: isFull
            ? ARCHIVING_STORAGE_ERROR_TITLE
            : ARCHIVING_STORAGE_INVALID_TITLE,
        },
        body: isFull
          ? ARCHIVING_STORAGE_ERROR_DESCRIPTION
          : ARCHIVING_STORAGE_INVALID_DESCRIPTION,
      });
    },
    [hideModal, showModal],
  );

  // Validate the archiving location before scheduling: block (with an error modal)
  // when the storage is full or the configured location cannot be read.
  const handleRunArchiveClick = useCallback(async () => {
    let status: ArchivingStorageStatus;
    try {
      const response = await new ArchivingService().fetchArchivingLocation();
      status =
        response.responseType === ResponseType.Success
          ? getArchivingStorageStatus(response.value)
          : 'invalid';
    } catch {
      status = 'invalid';
    }

    // Only full/invalid storage blocks scheduling; 'low' is a non-blocking warning.
    if (status === 'full' || status === 'invalid') {
      showStorageErrorModal(status);
      return;
    }

    handleArchiveClick();
  }, [handleArchiveClick, showStorageErrorModal]);

  const handlePauseConfirm = useCallback(async () => {
    hideModal();
    try {
      const result = await dispatch(pauseArchivingThunk()).unwrap();
      showAlertSnackBar(
        EAlertType.SUCCESS,
        ESnackBarPosition.TOP,
        result.message || 'Archiving job paused.',
      );
    } catch (error) {
      showAlertSnackBar(
        EAlertType.ERROR,
        ESnackBarPosition.TOP,
        typeof error === 'string' ? error : 'Failed to pause archiving job.',
      );
    }
  }, [dispatch, hideModal]);

  const handlePauseClick = useCallback(() => {
    showModal({
      heading: 'Pause Archiving',
      body: (
        <div>
          <FmlxBanner
            variant={FmlxBanner.Variant.WARNING}
            title={<div className="text-sm">{ARCHIVING_PAUSE_MODAL_WARNING_MESSAGE}</div>}
            show
            hideCloseButton
          />
          <div className="text-sm my-3 text-[#5D5D5D]">
            {ARCHIVING_PAUSE_MODAL_SUBTITLE}
          </div>
        </div>
      ),
      okButtonText: 'Confirm',
      noButtonText: 'Cancel',
      noButtonType: FmlxButton.Type.BASIC,
      useDefaultButtonLayout: true,
      onOk: handlePauseConfirm,
      hideCloseButton: true,
    });
  }, [handlePauseConfirm, showModal]);

  const handleResumeClick = useCallback(async () => {
    try {
      const result = await dispatch(resumeArchivingThunk()).unwrap();
      showAlertSnackBar(
        EAlertType.SUCCESS,
        ESnackBarPosition.TOP,
        result.message || 'Archiving job resumed.',
      );
    } catch (error) {
      showAlertSnackBar(
        EAlertType.ERROR,
        ESnackBarPosition.TOP,
        typeof error === 'string' ? error : 'Failed to resume archiving job.',
      );
    }
  }, [dispatch]);

  const handleStartOverConfirm = useCallback(
    async (restoreArchivedFiles: boolean) => {
      hideModal();
      try {
        const result = await dispatch(
          startOverArchivingThunk(restoreArchivedFiles),
        ).unwrap();
        dispatch(resetArchivingResults());
        onStartOverComplete?.();
        showAlertSnackBar(
          EAlertType.SUCCESS,
          ESnackBarPosition.TOP,
          result.message || 'Archiving job cancelled.',
        );
      } catch (error) {
        showAlertSnackBar(
          EAlertType.ERROR,
          ESnackBarPosition.TOP,
          typeof error === 'string' ? error : 'Failed to start over archiving job.',
        );
      }
    },
    [dispatch, hideModal, onStartOverComplete],
  );

  const handleStartOverClick = useCallback(() => {
    showModal({
      heading: ARCHIVING_START_OVER_TITLE,
      body: (
        <div>
          <FmlxBanner
            variant={FmlxBanner.Variant.WARNING}
            title={
              <div className="text-sm">{ARCHIVING_START_OVER_MODAL_WARNING_MESSAGE}</div>
            }
            show
            hideCloseButton
          />
          <div className="text-sm my-3 text-[#5D5D5D]">
            {ARCHIVING_START_OVER_MODAL_SUBTITLE}
          </div>
        </div>
      ),
      customFooter: (
        <ModalCheckboxFooter
          checkboxLabel={ARCHIVING_START_OVER_INCLUDE_ARCHIVED_CHECKBOX_LABEL}
          onCancel={hideModal}
          onConfirm={handleStartOverConfirm}
        />
      ),
      hideCloseButton: true,
    });
  }, [handleStartOverConfirm, showModal, hideModal]);

  const previewView = getPreviewTableView({
    isLoading: previewArchivingData.isLoading,
    hasSelection,
    rowCount: data.length,
  });

  useEffect(() => {
    if (previewView !== 'table') {
      setCounts({ total: 0, selected: 0, selectedExperimentIds: [] });
    }
  }, [previewView]);

  const storageNotice = getArchivingStorageNotice(storageStatus);

  return (
    <div className="tab-content-height w-full flex flex-col">
      {storageNotice && (
        <div
          data-testid="archiving-storage-notice"
          data-notice-type={storageNotice.type}
          className={`flex flex-none items-center justify-end gap-1 px-4 py-1 text-sm font-medium ${
            storageNotice.type === 'error' ? 'text-[#d32f2f]' : 'text-[#5c4a00]'
          }`}
        >
          <RMSvgIcon
            name={storageNotice.type === 'error' ? 'Error' : 'Warning'}
            height={16}
            width={16}
            fill={storageNotice.type === 'error' ? '#d32f2f' : '#f2c64b'}
          />
          <span>{storageNotice.message}</span>
        </div>
      )}
      <div className="basis-[95%] h-[90%] w-full min-h-0">
        <PreviewTableContent view={previewView} data={data} onCountsChange={setCounts} />
      </div>
      <div className="basis-[5%] w-full flex items-center border-t border-b border-gray-300 bg-white px-4 py-2 font-sans text-xs text-gray-700 select-none">
        <div>
          <span className="text-gray-500">Selected:</span>{' '}
          <span className="font-semibold text-gray-900">
            {counts.selected}/{counts.total}
          </span>
        </div>
        <div className="ml-auto flex items-center space-x-2">
          <FmlxButton
            disabled={!isJobPaused}
            onClick={handleStartOverClick}
            size="sm"
            label="Start Over"
          />
          <FmlxButton
            disabled={!isJobActive || isJobPaused}
            onClick={handlePauseClick}
            size="sm"
            label="Pause"
          />
          {isJobPaused ? (
            <FmlxButton
              onClick={handleResumeClick}
              size="sm"
              type={FmlxButton.Type.POSITIVE}
              label="Resume"
            />
          ) : (
            <FmlxButton
              disabled={counts.selected === 0 || isJobActive}
              onClick={handleRunArchiveClick}
              size="sm"
              type={FmlxButton.Type.POSITIVE}
              label="Run Archive"
            />
          )}
        </div>
      </div>
    </div>
  );
};

const PreviewTableLoading = () => (
  <div
    data-testid="preview-table-loading"
    className="flex h-full flex-col items-center justify-center gap-4"
  >
    <FmlxProgressBar type="circular" variant={FmlxProgressBar.Variant.INDETERMINATE} />
    <span className="font-sans text-base text-gray-600">Loading experiment data ...</span>
  </div>
);

const PreviewTableEmpty = () => (
  <div
    data-testid="preview-table-empty"
    className="flex flex-col h-full items-center justify-center text-xl text-[#737373]"
  >
    <span>No data shown</span>
    <span>Please determine archive parameter to show the data</span>
  </div>
);

const PreviewTableNoResults = () => (
  <div
    data-testid="preview-table-no-results"
    className="flex flex-col h-full items-center justify-center text-xl text-[#737373]"
  >
    <span>No Experiment to show from selected path</span>
  </div>
);

type PreviewTableContentProps = {
  view: PreviewTableView;
  data: Array<Record<string, unknown>>;
  onCountsChange: (counts: SelectionCounts) => void;
};

const PreviewTableContent = ({
  view,
  data,
  onCountsChange,
}: PreviewTableContentProps) => {
  switch (view) {
    case 'loading':
      return <PreviewTableLoading />;
    case 'empty':
      return <PreviewTableEmpty />;
    case 'noResults':
      return <PreviewTableNoResults />;
    case 'table':
      return (
        <CustomTable
          data={data}
          columns={PreviewTableColumns}
          id="preview-archive-table"
          autoSelectAll
          onCountsChange={onCountsChange}
          sortInfo={{ direction: 'asc', column: 'dateCreated' }}
          rowsPerPage={PREVIEW_TABLE_PAGE_SIZE}
        />
      );
  }
};
